import { EntityStatus, EventsData, FonterraPickups, Product, ProductEventsInData, VatState } from '@/api';
import { Pickup, JointPickup, JointPickupData } from './collections-types';
import { AxiosResponse } from 'axios';
import { VatModels } from '@/common/EmptyDistanceUtils';

/**
 * Matches pickups within an hour from Levno and Fonterra
 * @param levnoPickups Pickups that Levno has recorded, sorted by timestamp
 * @param fonterraPickups Pickups that Fonterra has recorded, sorted by timestamp
 * @param product Product collections are on - used to order vats
 * @returns An array of pickups combined by timestamp of pickup, +/- 1 hour
 */
export function matchPickups(
	levnoPickups: Pickup[] | undefined,
	fonterraPickups: Pickup[] | undefined,
	product: Product,
) {
	const timestampDelta = 3600;
	const jointPickups: JointPickup[] = [];
	if (!levnoPickups || !fonterraPickups) return [];
	const levnoPickupsSorted: Pickup[] | undefined = matchVats(
		filterFalsePickups(levnoPickups),
		product,
		true,
	);
	const fonterraPickupsSorted: Pickup[] | undefined = matchVats(fonterraPickups, product, false);
	if (!levnoPickupsSorted || !fonterraPickupsSorted) return [];

	levnoPickupsSorted.forEach((pickup: Pickup, index) => {
		jointPickups.push({
			vatId: pickup.vatId,
			tabLabel: pickup.tabLabel,
			vatNumber: fonterraPickupsSorted[index].vatNumber,
			pickups: [],
		});
		pickup.pickups.forEach(levnoPickup => {
			let matched = false;
			fonterraPickupsSorted[index].pickups.forEach(fonterraPickup => {
				if (
					!matched &&
					levnoPickup.start - timestampDelta < fonterraPickup.start &&
					levnoPickup.end + timestampDelta > fonterraPickup.end
				) {
					matched = true;
					jointPickups[index].pickups.push({ levno: levnoPickup, fonterra: fonterraPickup });
					fonterraPickupsSorted[index].pickups = fonterraPickupsSorted[index].pickups.filter(
						item => item !== fonterraPickup,
					);
				}
			});
			if (!matched) {
				jointPickups[index].pickups.push({ levno: levnoPickup, fonterra: undefined });
			}
		});
		fonterraPickupsSorted[index].pickups.forEach(fonterraPickup =>
			jointPickups[index].pickups.push({ levno: undefined, fonterra: fonterraPickup }),
		);
	});
	return jointPickups;
}

/**
 * Calculates volume and temperature differences, and finds the offset required to fix the difference in volume
 * @param pickupVats Array of fonterra and levno, matched up pickups
 * @param product Product that the pickups occurred on
 * @returns An array of joint pickups with differences in temperature and volume and required offset
 */
export function calculateDeltas(
	pickupVats: JointPickup[] | undefined,
	product: Product,
): JointPickup[] | undefined {
	// Helper function to find deltas to 1dp
	function getDelta(num1: number | undefined, num2: number | undefined): number | undefined {
		if (typeof num1 !== 'number' || typeof num2 !== 'number') return undefined;
		return Math.round((num1 - num2) * 10) / 10;
	}

	// Helper function to get the offset using the vatModels for the diameter
	function getOffset(
		delta: number | undefined,
		product: Product,
		index: number,
	): number | undefined {
		if (!product.vats || typeof delta !== 'number') return undefined;
		const vatModel = `${product.vats[index].formula}:${product.vats[index].sizeString}`;
		const vatModelInformation = VatModels.get(vatModel);

		if (!vatModelInformation) return undefined;
		const litresPerMM = Math.PI * Math.pow(vatModelInformation.diameter / 2 / 1000, 2);
		if (litresPerMM === 0 || isNaN(litresPerMM)) return undefined;
		return Math.round((delta / litresPerMM) * 100) / 100;
	}

	if (!pickupVats) return undefined;
	return pickupVats.map((pickupVat, index) => {
		return {
			...pickupVat,
			pickups: pickupVat.pickups.map(pickup => {
				const volumeDelta = getDelta(pickup.levno?.volume, pickup.fonterra?.volume);
				return {
					...pickup,
					delta: {
						volume: volumeDelta,
						temperature: getDelta(pickup.levno?.temperature, pickup.fonterra?.temperature),
						offset: getOffset(volumeDelta, product, index),
					},
				};
			}),
		};
	});
}

/**
 * Matches the vats together correctly in case they aren't matched. If fonterra vats do not show,
 * check that the fonterra and levno vats arrays are the same
 * @param pickups Array of pickups
 * @param product Product pickups are on
 * @param isLevno Boolean for if the pickups are from Levno
 */
export function matchVats(
	pickups: Pickup[] | undefined,
	product: Product,
	isLevno: boolean,
): Pickup[] | undefined {
	if (!pickups) return [];

	let orderedPickupsMap = new Map<number, Pickup | undefined>();
	let undefinedError = false;

	const pickupKey = isLevno ? 'vatId' : 'vatNumber';
	product.vats
		?.filter((vat) => vat.status === EntityStatus.Live && vat.state === VatState.Supply)
		?.forEach((vat, index) => {
			const key = isLevno ? vat.id : vat.vatNumber;

			let matchingPickup = pickups.find(pickup => {
				let match: number | string | undefined = pickup[pickupKey];
				if (typeof match === 'string') match = parseInt(match, 10);
				if (typeof match !== 'number') {
					undefinedError = true;
					return false;
				}
				return match === key;
			});

			if (matchingPickup) {
				orderedPickupsMap.set(index, {
					...matchingPickup,
					tabLabel: `${vat.label} (${vat.size}L)`,
				});
			}
		});
	// items in the map are possible to be undefined so we return if this is the case. This means there were
	// no pickups in the 60 days, or the amount of vats are unequal. If this happens, there is a product error
	if (undefinedError) return [];

	const returnPickups: Pickup[] = [];

	for (let index = 0; index < (product?.vats?.length ?? 0); index++) {
		const pickup = orderedPickupsMap.get(index);
		if (pickup) {
			returnPickups.push(pickup);
		}
	}
	return returnPickups.length > 0 ? returnPickups : undefined;
}

/**
 * Sorts pickups array by timestamp
 * @param pickups Array of pickups formatted into the Pickup interface
 * @returns Timestamp sorted pickup array
 */
export function sortPickups(pickups: Pickup[] | undefined): Pickup[] | undefined {
	if (!pickups) return undefined;
	pickups.forEach(pickup => {
		pickup.pickups.sort((a, b) => b.start - a.start);
	});
	return pickups;
}

/**
 * Filter out false pickups from levno data
 * @param pickups Array of pickups
 * @returns Array of pickups without false pickups
 */
export function filterFalsePickups(pickups: Pickup[] | undefined): Pickup[] | undefined {
	if (!pickups) return undefined;
	return pickups.map(pickupWrapper => {
		return {
			...pickupWrapper,
			pickups: pickupWrapper.pickups.filter(pickup => !pickup.falseDetection === true),
		};
	});
}

/**
 * Sorts joined pickups array by timestamp
 * @param jointPickups Array of joint pickups formatted into the JointPickup interface
 * @returns Timestamp sorted joint pickup array
 */
export function sortJointPickups(
	jointPickups: JointPickup[] | undefined,
): JointPickup[] | undefined {
	if (!jointPickups) return undefined;

	// Helper function to get the earliest starting time
	const getLatestStartTime = (pickupData: JointPickupData): number => {
		const levnoStart = pickupData.levno?.start ?? 0;
		const fonterraStart = pickupData.fonterra?.start ?? 0;
		return Math.max(levnoStart, fonterraStart);
	};

	jointPickups.forEach(jointPickup => {
		jointPickup.pickups.sort((a, b) => getLatestStartTime(b) - getLatestStartTime(a));
	});

	return jointPickups;
}

/**
 * Formats levno pickups into the Pickup interface
 * @param response API response with levno pickups
 * @returns Levno pickups formatted into the Pickup interface
 */
export function formatLevnoPickups(
	response: AxiosResponse<ProductEventsInData> | undefined,
): Pickup[] | undefined {
	return response?.data?.data?.map((vat: EventsData) => {
		return {
			vatId: vat.id,
			pickups:
				vat.pickupFinish?.map(({ start, finish, type, milkingInProgress, falseDetection }) => ({
					start: start?.timestamp ?? 0,
					end: finish?.timestamp ?? 0,
					temperature: start?.temperature ?? 0,
					volume:
						((start?.volume ?? 0) as number) -
						((type == 'full' ? 0 : finish?.volume ?? 0) as number),
					type,
					milkingInProgress: !!milkingInProgress,
					falseDetection,
				})) ?? [],
		};
	});
}

/**
 * Formats fonterra pickups into the Pickup interface
 * @param response API response with fonterra pickups
 * @returns Fonterra pickups formatted into the Pickup interface
 */
export function formatFonterraPickups(
	response: AxiosResponse<FonterraPickups[]> | undefined,
): Pickup[] | undefined {
	const pickups: Pickup[] = [];

	response?.data[0].pickup?.forEach(pickup => {
		pickup?.vat?.forEach((vat: any) => {
			const existingPickup = pickups.find(p => p.vatNumber === vat.id.toString());
			const pickupData = {
				start: pickup.startTime ?? 0,
				end: pickup.finishTime ?? 0,
				temperature: (vat.temperature as number) ?? 0,
				volume: vat.volume as number,
			};

			if (existingPickup) {
				existingPickup.pickups.push(pickupData);
			} else {
				pickups.push({
					vatNumber: vat.id.toString(),
					pickups: [pickupData],
				});
			}
		});
	});

	return pickups;
}
